import React, { useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DataTable from '../../components/ui/DataTable';
import FilterActions from '../../components/ui/FilterActions';
import FilterPanel from '../../components/ui/FilterPanel';
import IconActionButton from '../../components/ui/IconActionButton';
import TechnicalMaintenanceFormModal from './TechnicalMaintenanceFormModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import './technicalMaintenanceShared.css';
import { FLEX_WRAPPED_TEXT_CELL_CLASS, WrappedTextCell } from './tableCells';

const DEFAULT_VAT_RATE = 20;
const VAT_RATE_OPTIONS = [0, 1, 10, 20];

const emptyLine = { equipment: '', description: '', quantity: 1, unit_price: 0, amount: 0 };

const initialForm = {
  invoice_no: '',
  invoice_date: '',
  supplier: '',
  invoice_type: 'maintenance',
  subtotal: 0,
  vat_rate: DEFAULT_VAT_RATE,
  vat_amount: 0,
  total_amount: 0,
  currency: 'TRY',
  payment_status: 'pending',
  notes: '',
  lines: [{ ...emptyLine }],
};

const INVOICE_TYPES = [
  { value: 'maintenance', label: 'Bakım' },
  { value: 'spare_part', label: 'Yedek Parça' },
  { value: 'service', label: 'Servis' },
  { value: 'contract', label: 'Sözleşme' },
  { value: 'installation', label: 'Kurulum' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Bekliyor' },
  { value: 'paid', label: 'Ödendi' },
];

const defaultFilters = {
  search: '',
  invoice_type: '',
  payment_status: '',
  invoice_date: '',
};

const cellOrDash = (value) => (value ? value : '—');

const normalizeIsoDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

/** Tabloda gün/ay/yıl (ör. 01/02/2026) */
const formatDisplayDate = (value) => {
  const iso = normalizeIsoDate(value);
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return cellOrDash(value);
  return `${day}/${month}/${year}`;
};

const getEquipmentSummary = (row) => {
  if (row.equipment_summary) return row.equipment_summary;
  const names = (row.lines || []).map((line) => {
    if (line.equipment_name || line.equipment_code) {
      return [line.equipment_code, line.equipment_name].filter(Boolean).join(' — ');
    }
    return '';
  }).filter(Boolean);
  return [...new Set(names)].join(', ');
};

const getDescriptionSummary = (row) => {
  if (row.description_summary) return row.description_summary;
  return [...new Set(
    (row.lines || [])
      .map((line) => line.description?.trim())
      .filter(Boolean)
  )].join(', ');
};

const getInvoiceSearchText = (row) => {
  const parts = [
    row.invoice_no,
    row.supplier,
    row.notes,
    getEquipmentSummary(row),
    getDescriptionSummary(row),
  ];
  (row.lines || []).forEach((line) => {
    parts.push(line.description, line.equipment_name, line.equipment_code);
  });
  return parts.filter(Boolean).join(' ').toLowerCase();
};

const matchesInvoiceFilters = (row, activeFilters) => {
  if (activeFilters.invoice_type && row.invoice_type !== activeFilters.invoice_type) {
    return false;
  }
  if (activeFilters.payment_status && row.payment_status !== activeFilters.payment_status) {
    return false;
  }
  if (activeFilters.invoice_date
    && normalizeIsoDate(row.invoice_date) !== normalizeIsoDate(activeFilters.invoice_date)) {
    return false;
  }
  const searchTerm = activeFilters.search.trim().toLowerCase();
  if (searchTerm && !getInvoiceSearchText(row).includes(searchTerm)) {
    return false;
  }
  return true;
};

const toDecimal = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const normalized = String(value).replace(',', '.').trim();
  const num = Number(normalized);
  return Number.isFinite(num) ? num : fallback;
};

const inferVatRate = (subtotal, vatAmount) => {
  if (!subtotal) return DEFAULT_VAT_RATE;
  const exact = (toDecimal(vatAmount) / toDecimal(subtotal)) * 100;
  const match = VAT_RATE_OPTIONS.find((rate) => Math.abs(rate - exact) < 0.01);
  return match ?? Math.round(exact * 100) / 100;
};

const TechnicalMaintenanceInvoicesPage = () => {
  const { getFullName } = useAuth();
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoiceResponse, equipmentResponse] = await Promise.all([
        technicalMaintenanceApi.getInvoices(),
        technicalMaintenanceApi.getEquipment({ is_active: 'true' }),
      ]);
      const data = invoiceResponse?.data || [];
      setAllRows(data);
      setEquipment(equipmentResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Faturalar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setRows(allRows.filter((row) => matchesInvoiceFilters(row, filters)));
  }, [allRows, filters]);

  const equipmentOptions = useMemo(
    () => equipment.map((item) => ({
      value: item.id,
      label: [item.equipment_code, item.name, item.area].filter(Boolean).join(' — '),
    })),
    [equipment]
  );

  const columns = useMemo(() => [
    { header: 'Fatura No', accessorKey: 'invoice_no' },
    { header: 'Tarih', accessorKey: 'invoice_date', cell: ({ getValue }) => formatDisplayDate(getValue()) },
    { header: 'Tedarikçi', accessorKey: 'supplier', cell: ({ getValue }) => cellOrDash(getValue()) },
    { header: 'Ekipman', accessorKey: 'equipment_summary', cell: ({ row }) => cellOrDash(getEquipmentSummary(row.original)) },
    {
      header: 'Not',
      accessorKey: 'notes',
      cellClassName: FLEX_WRAPPED_TEXT_CELL_CLASS,
      cell: ({ getValue }) => <WrappedTextCell value={getValue()} />,
    },
    { header: 'Tip', accessorKey: 'invoice_type_label' },
    { header: 'Toplam', accessorKey: 'total_amount' },
    { header: 'Ödeme', accessorKey: 'payment_status_label' },
    {
      header: 'Giriş Yapan',
      accessorKey: 'created_by_name',
      size: 128,
      headerClassName: 'whitespace-nowrap min-w-[8rem]',
      cellClassName: 'whitespace-nowrap',
      cell: ({ getValue }) => cellOrDash(getValue()),
    },
    {
      header: 'İşlem',
      accessorKey: 'actions',
      cell: ({ row }) => (
        <div
          className="flex gap-2"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <IconActionButton icon={PencilSquareIcon} title="Düzenle" onClick={() => openEdit(row.original)} />
          <IconActionButton icon={TrashIcon} title="Sil" variant="danger" onClick={() => handleDelete(row.original)} />
        </div>
      ),
    },
  ], []);

  const recalcTotals = (lines, current) => {
    const subtotal = Number(lines.reduce((sum, line) => sum + toDecimal(line.amount), 0).toFixed(2));
    const vatRate = toDecimal(current.vat_rate, DEFAULT_VAT_RATE);
    const vat = Number((subtotal * (vatRate / 100)).toFixed(2));
    return {
      ...current,
      subtotal,
      vat_rate: vatRate,
      vat_amount: vat,
      total_amount: Number((subtotal + vat).toFixed(2)),
      lines,
    };
  };

  const updateLine = (index, patch) => {
    const lines = formData.lines.map((line, i) => {
      if (i !== index) return line;
      const next = { ...line, ...patch };
      const qty = toDecimal(next.quantity, 1);
      const unit = toDecimal(next.unit_price);
      next.quantity = qty;
      next.unit_price = unit;
      next.amount = Number((qty * unit).toFixed(2));
      return next;
    });
    setFormData(recalcTotals(lines, formData));
  };

  const openCreate = () => {
    setEditingRow(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  const openEdit = async (row) => {
    setEditingRow(row);
    try {
      const response = await technicalMaintenanceApi.getInvoice(row.id);
      const data = response?.data || row;
      setFormData({
        invoice_no: data.invoice_no || '',
        invoice_date: data.invoice_date || '',
        supplier: data.supplier || '',
        invoice_type: data.invoice_type || 'maintenance',
        subtotal: data.subtotal || 0,
        vat_rate: inferVatRate(data.subtotal, data.vat_amount),
        vat_amount: data.vat_amount || 0,
        total_amount: data.total_amount || 0,
        currency: data.currency || 'TRY',
        payment_status: data.payment_status || 'pending',
        notes: data.notes || '',
        lines: (data.lines && data.lines.length ? data.lines : [{ ...emptyLine }]).map((line) => ({
          equipment: line.equipment || '',
          description: line.description || '',
          quantity: line.quantity || 1,
          unit_price: line.unit_price || 0,
          amount: line.amount || 0,
        })),
      });
      setIsModalOpen(true);
    } catch (err) {
      setError(err.message || 'Fatura detayı yüklenemedi.');
    }
  };

  const handleSave = async () => {
    if (!formData.invoice_no.trim() || !formData.invoice_date) {
      setError('Fatura no ve tarih zorunludur.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        invoice_no: formData.invoice_no.trim(),
        invoice_date: formData.invoice_date,
        supplier: formData.supplier,
        invoice_type: formData.invoice_type,
        currency: formData.currency,
        payment_status: formData.payment_status,
        notes: formData.notes,
        subtotal: toDecimal(formData.subtotal),
        vat_amount: toDecimal(formData.vat_amount),
        total_amount: toDecimal(formData.total_amount),
        lines: formData.lines.map((line) => ({
          description: line.description?.trim() || 'Genel',
          equipment: line.equipment ? Number(line.equipment) : null,
          quantity: toDecimal(line.quantity, 1),
          unit_price: toDecimal(line.unit_price),
          amount: toDecimal(line.amount),
        })),
      };
      if (editingRow) {
        await technicalMaintenanceApi.updateInvoice(editingRow.id, payload);
      } else {
        await technicalMaintenanceApi.createInvoice(payload);
      }
      setIsModalOpen(false);
      await loadData(filters);
    } catch (err) {
      setError(err.message || 'Fatura kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.invoice_no}" silinsin mi?`)) return;
    try {
      await technicalMaintenanceApi.deleteInvoice(row.id);
      await loadData(filters);
    } catch (err) {
      setError(err.message || 'Fatura silinemedi.');
    }
  };

  const handleSearch = () => {
    setError(null);
    setFilters({ ...draftFilters });
  };

  const handleClearFilters = () => {
    setDraftFilters(defaultFilters);
    setFilters(defaultFilters);
  };

  return (
    <div className="tm-page space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Fatura Girişi</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Bakım, servis ve yedek parça faturaları.</p>
      </div>

      <ActionToolbar>
        <Button onClick={openCreate}>
          <PlusIcon className="h-4 w-4 mr-1 inline" />
          Yeni Fatura
        </Button>
      </ActionToolbar>

      <FilterPanel isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((v) => !v)}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <Label>Ara</Label>
            <Input
              value={draftFilters.search}
              onChange={(e) => setDraftFilters({ ...draftFilters, search: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              placeholder="Fatura no, tedarikçi, ekipman, not..."
            />
          </div>
          <div>
            <Label>Fatura Tipi</Label>
            <Select
              value={draftFilters.invoice_type}
              onChange={(e) => setDraftFilters({ ...draftFilters, invoice_type: e.target.value })}
            >
              <option value="">Tümü</option>
              {INVOICE_TYPES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Ödeme Durumu</Label>
            <Select
              value={draftFilters.payment_status}
              onChange={(e) => setDraftFilters({ ...draftFilters, payment_status: e.target.value })}
            >
              <option value="">Tümü</option>
              {PAYMENT_STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Tarih</Label>
            <Input
              type="date"
              value={draftFilters.invoice_date}
              onChange={(e) => setDraftFilters({ ...draftFilters, invoice_date: e.target.value })}
            />
          </div>
        </div>
        <FilterActions onSearch={handleSearch} onClear={handleClearFilters} />
        {(filters.search || filters.invoice_type || filters.payment_status || filters.invoice_date) && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {rows.length} fatura listeleniyor{allRows.length !== rows.length ? ` (${allRows.length} kayıttan)` : ''}
          </p>
        )}
      </FilterPanel>

      {error && <div className="text-red-600 text-sm">{error}</div>}
      <DataTable
        columns={columns}
        data={rows}
        isLoading={loading}
        showSearch={false}
        fixedHorizontalScroll
        onRowClick={openEdit}
      />

      <TechnicalMaintenanceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRow ? 'Fatura Düzenle' : 'Yeni Fatura'}
        wide
        footer={(
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Fatura No</Label>
              <Input value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} />
            </div>
            <div>
              <Label>Tarih</Label>
              <Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Tedarikçi</Label>
              <Input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} />
            </div>
            <div>
              <Label>Fatura Tipi</Label>
              <Select
                value={formData.invoice_type}
                onChange={(e) => setFormData({ ...formData, invoice_type: e.target.value })}
              >
                {INVOICE_TYPES.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Select>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Fatura Kalemleri</Label>
              <Button
                variant="secondary"
                onClick={() => setFormData(recalcTotals([...formData.lines, { ...emptyLine }], formData))}
              >
                Kalem Ekle
              </Button>
            </div>
            <div className="hidden md:grid md:grid-cols-5 gap-2 px-1">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Ekipman (opsiyonel)</Label>
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Açıklama</Label>
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Adet</Label>
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Birim Fiyat</Label>
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Tutar</Label>
            </div>
            {formData.lines.map((line, index) => (
              <div key={`line-${index}`} className="grid grid-cols-1 md:grid-cols-5 gap-2 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                <div>
                  <Label className="md:hidden mb-1 text-xs">Ekipman (opsiyonel)</Label>
                  <SearchableSelect
                    options={equipmentOptions}
                    value={line.equipment}
                    onChange={(nextValue) => updateLine(index, { equipment: nextValue })}
                    placeholder="Kod, ad veya alan yazın..."
                  />
                </div>
                <div>
                  <Label className="md:hidden mb-1 text-xs">Açıklama</Label>
                  <Input
                    placeholder="Örn: Filtre değişimi"
                    value={line.description}
                    onChange={(e) => updateLine(index, { description: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="md:hidden mb-1 text-xs">Adet</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="1"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="md:hidden mb-1 text-xs">Birim Fiyat</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={line.unit_price}
                    onChange={(e) => updateLine(index, { unit_price: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="md:hidden mb-1 text-xs">Tutar</Label>
                  <Input value={line.amount} readOnly title="Adet × Birim Fiyat" />
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div>
              <Label>Ara Toplam</Label>
              <Input value={formData.subtotal} readOnly />
            </div>
            <div>
              <Label>KDV Oranı</Label>
              <Select
                value={String(formData.vat_rate)}
                onChange={(e) => setFormData(recalcTotals(formData.lines, { ...formData, vat_rate: e.target.value }))}
              >
                {VAT_RATE_OPTIONS.map((rate) => (
                  <option key={rate} value={rate}>%{rate}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>KDV Tutarı</Label>
              <Input value={formData.vat_amount} readOnly title="Ara toplam × KDV oranı" />
            </div>
            <div>
              <Label>Genel Toplam</Label>
              <Input value={formData.total_amount} readOnly />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Ödeme Durumu</Label>
              <Select
                value={formData.payment_status}
                onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
              >
              {PAYMENT_STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
              </Select>
            </div>
            <div>
              <Label>Giriş Yapan</Label>
              <Input
                value={editingRow?.created_by_name || getFullName() || '—'}
                readOnly
                disabled
                className="bg-gray-50 dark:bg-gray-800/60 cursor-not-allowed"
              />
            </div>
          </div>
          <div>
            <Label>Not</Label>
            <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
          </div>
        </div>
      </TechnicalMaintenanceFormModal>
    </div>
  );
};

export default TechnicalMaintenanceInvoicesPage;
